import React, { useState, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import testimage from '../assets/images/_.gif';
// import success from '../assets/svgs/image 64.svg';
import uploadIcon from '../assets/svgs/folder-arrow-down.svg';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = 'https://beat-fussion-backend.onrender.com/api';
// const API_BASE_URL = 'https://api.beatsfussion.com/api';

type SignUpType = 'viewer' | 'team';
type Stage = 'signup' | 'otp' | 'application';

interface ViewerForm {
  firstName: string;
  lastName: string;
  email: string;
  referralCode: string;
  password: string;
}

interface TeamMember {
  id: number;
  name: string;
  file?: File;
}

interface TeamForm {
  teamEmail: string;
  password: string;
  teamName: string;
  teamOrganization: string;
  instagram: string;
  youtube: string;
  tiktok: string;
  facebook: string;
  members: TeamMember[];
  managerName: string;
  managerFile?: File;
  videoFile?: File;
}

interface ApiErrorResponse {
  error?: string;
  message?: string;
  detail?: string;
}

// 🔔 Unified NotificationModal
const NotificationModal: React.FC<{
  type: 'error' | 'success';
  message: string;
  onClose: () => void;
}> = ({ type, message, onClose }) => {
  const isError = type === 'error';
  const bg = isError ? 'bg-red-100' : 'bg-green-100';
  const border = isError ? 'border-red-400' : 'border-green-400';
  const text = isError ? 'text-red-700' : 'text-green-700';
  const title = isError ? 'Error' : 'Success';

  // Auto-close after 3.5s
  React.useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 flex items-start justify-center z-50">
      <div className="absolute inset-0 bg-black bg-opacity-25" onClick={onClose} />
      <div className={`mt-16 z-10 rounded-lg shadow-lg border ${border} ${bg} p-5 max-w-sm w-full`}>
        <h3 className={`font-semibold text-lg mb-1 ${text}`}>{title}</h3>
        <p className="text-gray-800 text-base">{message}</p>
      </div>
    </div>
  );
};


const SignUp: React.FC = () => {
  const [mode, setMode] = useState<SignUpType>('viewer');
  const [stage, setStage] = useState<Stage>('signup');
  const [showPassword, setShowPassword] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [notification, setNotification] = useState<{ type: 'error' | 'success'; message: string } | null>(null);
  const [teamSuccess, setTeamSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();


  const [viewerData, setViewerData] = useState<ViewerForm>({
    firstName: '',
    lastName: '',
    email: '',
    referralCode: '',
    password: '',
  });

  const [teamData, setTeamData] = useState<TeamForm>({
    teamEmail: '',
    password: '',
    teamName: '',
    teamOrganization: '',
    instagram: '',
    youtube: '',
    tiktok: '',
    facebook: '',
    members: [{ id: Date.now(), name: '' }],
    managerName: '',
    managerFile: undefined,
    videoFile: undefined,
  });

  const handleViewerChange = (e: ChangeEvent<HTMLInputElement>) =>
    setViewerData({ ...viewerData, [e.target.name]: e.target.value });

  const handleTeamChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, type, value, files } = e.target;
    if (type === 'file' && files) setTeamData({ ...teamData, [name]: files[0] });
    else setTeamData({ ...teamData, [name]: value });
  };

  const addMember = () => {
    setTeamData(prev => ({
      ...prev,
      members: [...prev.members, { id: Date.now(), name: '' }],
    }));
  };

  const removeMember = (id: number) => {
    setTeamData(prev => ({
      ...prev,
      members: prev.members.filter(m => m.id !== id),
    }));
  };

  // ────────────────────────────────
  // API Calls
  // ────────────────────────────────

  const handleSubmitViewer = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await axios.post(
        `${API_BASE_URL}/viewers/sign-up/`,
        {
          first_name: viewerData.firstName,
          last_name: viewerData.lastName,
          email: viewerData.email,
          referral_code: viewerData.referralCode,
          password: viewerData.password,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setNotification({ type: 'success', message: 'Viewer signup successful!' });
      login({
        name: viewerData.lastName || 'Viewer',
        role: 'viewer',
        token: 'none',
      });
      navigate('/');
      console.log('✅ Viewer signup success');
    } catch (err) {
      let msg = 'An unexpected error occurred.';
      if (axios.isAxiosError<ApiErrorResponse>(err) && err.response) {
        const d = err.response.data;
        msg = d.error ?? d.message ?? d.detail ?? err.message;
      }
      setNotification({ type: 'error', message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitTeamSignup = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_BASE_URL}/team/sign-up/`,
        { email: teamData.teamEmail, password: teamData.password },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const loginRes = await axios.post(
        `${API_BASE_URL}/team/login/`,
        { email: teamData.teamEmail, password: teamData.password },
        { headers: { 'Content-Type': 'application/json' } }
      );

      console.log('🔑 login response:', loginRes.data);

      const token = loginRes.data.access_token;
      if (!token) throw new Error('No access token returned from server');

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // 🔹 Request OTP
      await axios.post(`${API_BASE_URL}/team/request-otp-verify-email/`, {
        email: teamData.teamEmail,
      });
      setNotification({ type: 'success', message: 'OTP has been sent to your email!' });
      console.log('📩 OTP requested successfully');
      setStage('otp');
    } catch (err) {
      let msg = 'An unexpected error occurred.';
      if (axios.isAxiosError<ApiErrorResponse>(err) && err.response) {
        const d = err.response.data;
        msg = d.error ?? d.message ?? d.detail ?? err.message;
      }
      setNotification({ type: 'error', message: msg });
    }
  };

  const handleSubmitOTP = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await axios.put(`${API_BASE_URL}/team/request-otp-verify-email/`, {
        email: teamData.teamEmail,
        otp: otpCode,
      });
      setNotification({ type: 'success', message: 'Email verified successfully!' });
      console.log('✅ OTP verified successfully');
      setTeamSuccess(true);
      setStage('application');
    } catch (err) {
      let msg = 'Invalid OTP or verification failed.';
      if (axios.isAxiosError<ApiErrorResponse>(err) && err.response) {
        const d = err.response.data;
        msg = d.error ?? d.message ?? d.detail ?? err.message;
      }
      setNotification({ type: 'error', message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitApplication = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');

      const payload = {
        team_name: teamData.teamName,
        team_org: teamData.teamOrganization,
        team_members: teamData.members.map(m => ({ fullname: m.name })),
        manager_info: [{ manager_name: teamData.managerName }],
      };
      const res = await axios.post(
        `${API_BASE_URL}/team/create-team/`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setNotification({ type: 'success', message: 'Team application submitted successfully!' });
      login({
        name: teamData.teamName || 'Team',
        role: 'team',
        token: localStorage.getItem('token') || '',
      });
      navigate('/');
      console.log('✅ create-team response:', res.data);
    } catch (err) {
      let msg = 'An unexpected error occurred.';
      if (axios.isAxiosError<ApiErrorResponse>(err) && err.response) {
        const d = err.response.data;
        msg = d.error ?? d.message ?? d.detail ?? err.message;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setNotification({ type: 'error', message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  // ────────────────────────────────
  // UI Rendering
  // ────────────────────────────────

  return (
    <>
      {notification && (
        <NotificationModal
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="min-h-screen bg-white flex items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="flex flex-col md:flex-row w-full max-w-5xl bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">

          {/* LEFT COLUMN */}
          <div className="hidden md:flex md:w-1/2 bg-purple-100 items-center justify-center p-6 sm:p-8 md:p-10 m-2 sm:m-2 md:pm-2 rounded-2xl">
            <div className="text-center">
              <img src={testimage} alt="Dance illustration" className="mb-6 w-40 sm:w-48 md:w-56 mx-auto" />
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-purple-700 mb-2">
                Let’s get your groove on
              </h2>
              <p className="text-purple-600 text-sm sm:text-base">
                Join the BeatFusion community—support dancers or showcase your crew!
              </p>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="w-full md:w-1/2 flex items-center justify-center p-6 sm:p-8 md:p-10">
            <div className="w-full max-w-md space-y-6">

              {/* ───────────────────── SIGNUP STAGE ───────────────────── */}
              {stage === 'signup' && (
                <>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setMode('viewer'); setTeamSuccess(false); }}
                      className={`flex-1 py-2 font-semibold rounded-lg transition ${
                        mode === 'viewer'
                          ? 'bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Viewer
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMode('team'); setTeamSuccess(false); }}
                      className={`flex-1 py-2 font-semibold rounded-lg transition ${
                        mode === 'team'
                          ? 'bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Team
                    </button>
                  </div>

                  {/* VIEWER SIGN-UP FORM */}
                  {mode === 'viewer' && (
                    <form onSubmit={handleSubmitViewer} className="space-y-4">
                      <input name="firstName" placeholder="First name" value={viewerData.firstName} onChange={handleViewerChange} required className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                      <input name="lastName" placeholder="Last name" value={viewerData.lastName} onChange={handleViewerChange} required className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                      <input name="email" type="email" placeholder="Email" value={viewerData.email} onChange={handleViewerChange} required className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                      <input name="referralCode" placeholder="Referral Code" value={viewerData.referralCode} onChange={handleViewerChange} required className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                      <div className="relative">
                        <input
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Password"
                          value={viewerData.password}
                          onChange={handleViewerChange}
                          required
                          className="w-full text-base px-4 py-2 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(v => !v)}
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? (
                            // 👁️ eye-off icon
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                              viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M13.875 18.825A10.05 10.05 0 0112 19
                                  c-4.477 0-8.268-2.943-9.542-7
                                  a9.956 9.956 0 012.223-3.428M6.18 6.18A9.956 9.956 0 0112 5
                                  c4.477 0 8.268 2.943 9.542 7
                                  -.162.516-.363 1.02-.597 1.503M3 3l18 18" />
                            </svg>
                          ) : (
                            // 👁️ eye icon
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                              viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5
                                  c4.477 0 8.268 2.943 9.542 7
                                  -1.274 4.057-5.065 7-9.542 7
                                  -4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-3 font-semibold rounded-lg bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] text-white transition ${
                          isLoading ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'
                        }`}
                      >
                        {isLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg
                              className="animate-spin h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 010 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                              ></path>
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          'Sign up as Viewer'
                        )}
                      </button>
                      <p className="text-center text-sm text-gray-600">
                        Already have an account? <Link to="/signin" className="text-[#9F00FF] hover:underline font-medium">Sign In</Link>
                      </p>
                      <p className="text-center text-sm text-gray-600">
                        By creating an account, you agree with our{' '}
                        <Link to="/terms" className="text-[#9F00FF] hover:underline font-medium">Terms of Service</Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-[#9F00FF] hover:underline font-medium">Privacy Policy</Link>.
                      </p>
                    </form>
                  )}

                  {/* TEAM SIGN-UP FORM */}
                  {mode === 'team' && !teamSuccess && (
                    <form onSubmit={handleSubmitTeamSignup} className="space-y-4">
                      <input name="teamEmail" type="email" placeholder="Team email" value={teamData.teamEmail} onChange={handleTeamChange} required className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                      <div className="relative">
                        <input
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Password"
                          value={teamData.password}
                          onChange={handleTeamChange}
                          required
                          className="w-full text-base px-4 py-2 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(v => !v)}
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                              viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M13.875 18.825A10.05 10.05 0 0112 19
                                  c-4.477 0-8.268-2.943-9.542-7
                                  a9.956 9.956 0 012.223-3.428M6.18 6.18A9.956 9.956 0 0112 5
                                  c4.477 0 8.268 2.943 9.542 7
                                  -.162.516-.363 1.02-.597 1.503M3 3l18 18" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                              viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5
                                  c4.477 0 8.268 2.943 9.542 7
                                  -1.274 4.057-5.065 7-9.542 7
                                  -4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-3 font-semibold rounded-lg bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] text-white transition ${
                          isLoading ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'
                        }`}
                      >
                        {isLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 010 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                              ></path>
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          'Sign up as Team'
                        )}
                      </button>
                      <p className="text-center text-sm text-gray-600">
                        Already have an account? <Link to="/signin" className="text-[#9F00FF] hover:underline font-medium">Sign In</Link>
                      </p>
                      <p className="text-center text-sm text-gray-600">
                        By creating an account, you agree with our{' '}
                        <Link to="/terms" className="text-[#9F00FF] hover:underline font-medium">Terms of Service</Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-[#9F00FF] hover:underline font-medium">Privacy Policy</Link>.
                      </p>
                    </form>
                  )}
                </>
              )}

              {/* ───────────────────── OTP STAGE ───────────────────── */}
              {stage === 'otp' && (
                <form onSubmit={handleSubmitOTP} className="space-y-6 text-center">
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="#9F00FF" className="w-10 h-10">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25H4.5a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0L12 13.5 2.25 6.75m19.5 0A2.25 2.25 0 0019.5 4.5H4.5a2.25 2.25 0 00-2.25 2.25" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900">Check your email</h3>
                    <p className="text-gray-600 text-sm">
                      Enter the 4-digit code sent to <br />
                      <span className="font-medium text-gray-800">{teamData.teamEmail}</span>
                    </p>
                  </div>

                  {/* OTP input boxes */}
                  <div className="flex justify-center gap-3">
                    {Array(4).fill(0).map((_, i) => (
                      <input
                        key={i}
                        type="text"
                        maxLength={1}
                        value={otpCode[i] || ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          if (!val && otpCode.length > 0) {
                            setOtpCode(prev => prev.slice(0, i) + val + prev.slice(i + 1));
                            const prevInput = document.getElementById(`otp-${i - 1}`);
                            prevInput && (prevInput as HTMLInputElement).focus();
                          } else if (val) {
                            setOtpCode(prev => prev.slice(0, i) + val + prev.slice(i + 1));
                            const nextInput = document.getElementById(`otp-${i + 1}`);
                            nextInput && (nextInput as HTMLInputElement).focus();
                          }
                        }}
                        id={`otp-${i}`}
                        className="w-12 h-12 text-center text-xl border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9F00FF]"
                      />
                    ))}
                  </div>

                  <p className="text-sm text-gray-600">
                    Didn’t get a code?{' '}
                   <button
                      type="button"
                      onClick={async () => {
                        try {
                          await axios.post(`${API_BASE_URL}/team/request-otp-verify-email/`, { email: teamData.teamEmail });
                          setNotification({ type: 'success', message: 'A new code has been sent to your email!' });
                        } catch (err) {
                          let msg = 'Failed to resend code. Please try again.';
                          if (axios.isAxiosError<ApiErrorResponse>(err) && err.response) {
                            const d = err.response.data;
                            msg = d.error ?? d.message ?? d.detail ?? err.message;
                          }
                          setNotification({ type: 'error', message: msg });
                        }
                      }}
                      className="text-[#9F00FF] hover:underline font-medium"
                    >
                      Resend
                    </button>
                  </p>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full py-3 font-semibold rounded-lg bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] text-white transition ${
                      isLoading ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'
                    }`}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 010 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                          ></path>
                        </svg>
                        Verifying...
                      </span>
                    ) : (
                      'Verify & Continue'
                    )}
                  </button>
                </form>
              )}

              {/* ───────────────────── APPLICATION STAGE ───────────────────── */}
              {stage === 'application' && (
                <form onSubmit={handleSubmitApplication} className="space-y-4">
                  <h3 className="text-2xl font-semibold">Team Application</h3>

                  <input name="teamName" placeholder="Team name" value={teamData.teamName} onChange={handleTeamChange} required className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                  <input name="teamOrganization" placeholder="Team organization" value={teamData.teamOrganization} onChange={handleTeamChange} className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input name="instagram" placeholder="Instagram URL" value={teamData.instagram} onChange={handleTeamChange} className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                    <input name="youtube" placeholder="YouTube URL" value={teamData.youtube} onChange={handleTeamChange} className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                    <input name="tiktok" placeholder="TikTok URL" value={teamData.tiktok} onChange={handleTeamChange} className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                    <input name="facebook" placeholder="Facebook URL" value={teamData.facebook} onChange={handleTeamChange} className="w-full text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                  </div>

                  {teamData.members.map(m => (
                    <div key={m.id} className="flex items-center space-x-2">
                      <input
                        type="text"
                        placeholder="Member name"
                        value={m.name}
                        onChange={e => {
                          const name = e.target.value;
                          setTeamData(prev => ({
                            ...prev,
                            members: prev.members.map(x => (x.id === m.id ? { ...x, name } : x)),
                          }));
                        }}
                        className="flex-1 text-base px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-400"
                      />
                      <label htmlFor={`memberFile-${m.id}`} className="p-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                        <img src={uploadIcon} alt="Upload" className="w-5 h-5" />
                      </label>
                      <input
                        id={`memberFile-${m.id}`}
                        type="file"
                        accept="image/*"
                        onChange={e => {
                          if (!e.target.files) return;
                          const file = e.target.files[0];
                          setTeamData(prev => ({
                            ...prev,
                            members: prev.members.map(x => (x.id === m.id ? { ...x, file } : x)),
                          }));
                        }}
                        className="hidden"
                      />
                      {teamData.members.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeMember(m.id)}
                          className="text-red-500 hover:underline text-base"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}

                  <button type="button" onClick={addMember} className="w-full py-2 bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] text-white rounded-lg">
                    + Add member
                  </button>

                  <div className="flex items-center space-x-2">
                    <input name="managerName" placeholder="Manager’s name" value={teamData.managerName} onChange={handleTeamChange} className="flex-1 text-base px-4 py-2 mb-2 border rounded-lg focus:ring-2 focus:ring-purple-400" />
                    <label htmlFor="managerFile" className="p-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                      <img src={uploadIcon} alt="Upload" className="w-5 h-5" />
                    </label>
                    <input
                      id="managerFile"
                      name="managerFile"
                      type="file"
                      accept="image/*"
                      onChange={e => {
                        if (e.target.files) {
                          setTeamData({ ...teamData, managerFile: e.target.files[0] });
                        }
                      }}
                      className="hidden"
                    />
                  </div>

                  <label>
                    <input
                      id="videoFile"
                      name="videoFile"
                      type="file"
                      accept="video/*"
                      onChange={e => {
                        if (e.target.files) {
                          setTeamData({ ...teamData, videoFile: e.target.files[0] });
                        }
                      }}
                      className="hidden"
                    />
                    <span className="inline-block w-full text-base text-center py-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                      Upload Video
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full py-3 font-semibold rounded-lg bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] text-white transition ${
                      isLoading ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'
                    }`}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 010 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                          ></path>
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;
